export const escapeSoqlString = (input: string): string => {
  if (!input) return ''
  return (
    input
      // Escape backslash first
      .replace(/\\/g, '\\\\')
      // Escape double quote
      .replace(/"/g, '\\"')
      // Escape single quote
      .replace(/'/g, "\\'")
      // Escape percent (LIKE)
      .replace(/%/g, '\\%')
      // Escape underscore (LIKE)
      .replace(/_/g, '\\_')
      // Escape new line (\n or \N)
      .replace(/\n/gi, '\\n')
      // Escape carriage return (\r or \R)
      .replace(/\r/gi, '\\r')
      // Escape tab (\t or \T)
      .replace(/\t/gi, '\\t')
      // Escape bell (\b or \B)
      // eslint-disable-next-line no-control-regex
      .replace(/\x07/gi, '\\b')
      // Escape form feed (\f or \F)
      .replace(/\f/gi, '\\f')
  )
}

interface WhereClause {
  type: 'AND' | 'OR' | 'NOT' | 'CONDITION' | 'GROUP'
  left?: WhereClause | null
  right?: WhereClause | null
  condition?: ConditionItem
  parentheses?: boolean
}

interface ConditionItem {
  field: string
  operator: string
  value: any
}

interface Token {
  type: 'FIELD' | 'OPERATOR' | 'VALUE' | 'AND' | 'OR' | 'NOT' | 'LPAREN' | 'RPAREN' | 'COMMA'
  value: string
  position: number
}

/**
 * Safely removes quotes from a token value if they exist
 */
function unquoteTokenValue(tokenValue: string): string {
  if (
    tokenValue.length >= 2 &&
    ((tokenValue.startsWith("'") && tokenValue.endsWith("'")) ||
      (tokenValue.startsWith('"') && tokenValue.endsWith('"')))
  ) {
    return tokenValue.slice(1, -1)
  }
  return tokenValue
}

function tokenizeWhereClause(whereClause: string): Token[] {
  const tokens: Token[] = []
  let i = 0

  while (i < whereClause.length) {
    const char = whereClause[i]

    // Skip whitespace
    if (/\s/.test(char)) {
      i++
      continue
    }

    // Handle parentheses
    if (char === '(') {
      tokens.push({ type: 'LPAREN', value: '(', position: i })
      i++
      continue
    }

    if (char === ')') {
      tokens.push({ type: 'RPAREN', value: ')', position: i })
      i++
      continue
    }

    // Handle operators
    if (char === '=') {
      tokens.push({ type: 'OPERATOR', value: '=', position: i })
      i++
      continue
    }

    if (char === '!') {
      if (whereClause[i + 1] === '=') {
        tokens.push({ type: 'OPERATOR', value: '!=', position: i })
        i += 2
        continue
      }
    }

    // Handle IN operator (with word boundary check)
    if (
      whereClause.substring(i, i + 2).toUpperCase() === 'IN' &&
      (i === 0 || /\s/.test(whereClause[i - 1])) &&
      (i + 2 >= whereClause.length || /\s/.test(whereClause[i + 2]))
    ) {
      tokens.push({ type: 'OPERATOR', value: 'IN', position: i })
      i += 2
      continue
    }

    // Handle LIKE operator (with word boundary check)
    if (
      whereClause.substring(i, i + 4).toUpperCase() === 'LIKE' &&
      (i === 0 || /\s/.test(whereClause[i - 1])) &&
      (i + 4 >= whereClause.length || /\s/.test(whereClause[i + 4]))
    ) {
      tokens.push({ type: 'OPERATOR', value: 'LIKE', position: i })
      i += 4
      continue
    }

    // Handle AND operator (with word boundary check)
    if (
      whereClause.substring(i, i + 3).toUpperCase() === 'AND' &&
      (i === 0 || /\s/.test(whereClause[i - 1])) &&
      (i + 3 >= whereClause.length || /\s/.test(whereClause[i + 3]))
    ) {
      tokens.push({ type: 'AND', value: 'AND', position: i })
      i += 3
      continue
    }

    // Handle OR operator (with word boundary check)
    if (
      whereClause.substring(i, i + 2).toUpperCase() === 'OR' &&
      (i === 0 || /\s/.test(whereClause[i - 1])) &&
      (i + 2 >= whereClause.length || /\s/.test(whereClause[i + 2]))
    ) {
      tokens.push({ type: 'OR', value: 'OR', position: i })
      i += 2
      continue
    }

    // Handle NOT operator (with word boundary check)
    if (
      whereClause.substring(i, i + 3).toUpperCase() === 'NOT' &&
      (i === 0 || /\s/.test(whereClause[i - 1])) &&
      (i + 3 >= whereClause.length || /\s/.test(whereClause[i + 3]))
    ) {
      tokens.push({ type: 'NOT', value: 'NOT', position: i })
      i += 3
      continue
    }

    // Handle comma
    if (char === ',') {
      tokens.push({ type: 'COMMA', value: ',', position: i })
      i++
      continue
    }

    // Handle quoted strings
    if (char === "'" || char === '"') {
      const quote = char
      let value = char
      i++

      while (i < whereClause.length && whereClause[i] !== quote) {
        value += whereClause[i]
        i++
      }

      if (i < whereClause.length) {
        value += whereClause[i]
        i++
      }

      tokens.push({ type: 'VALUE', value: value, position: i - value.length })
      continue
    }

    // Handle field names and other identifiers
    let value = ''
    while (i < whereClause.length && /[a-zA-Z0-9_.]/.test(whereClause[i])) {
      value += whereClause[i]
      i++
    }

    if (value) {
      // Determine if this is a field or value based on context
      // If the previous token was an operator, this is likely a value
      const isValue = tokens.length > 0 && tokens[tokens.length - 1].type === 'OPERATOR'
      tokens.push({
        type: isValue ? 'VALUE' : 'FIELD',
        value: value,
        position: i - value.length,
      })
      continue
    }

    // Skip unknown characters
    i++
  }

  return tokens
}

function parseWhereClause(whereClause: string): WhereClause | null {
  const tokens = tokenizeWhereClause(whereClause)
  let currentToken = 0

  function parseExpression(): WhereClause | null {
    return parseOr()
  }

  function parseOr(): WhereClause | null {
    let left = parseAnd()

    while (currentToken < tokens.length && tokens[currentToken].type === 'OR') {
      currentToken++ // consume OR
      const right = parseAnd()
      if (!right) return null

      left = {
        type: 'OR',
        left,
        right,
      }
    }

    return left
  }

  function parseAnd(): WhereClause | null {
    let left = parseNot()

    while (currentToken < tokens.length && tokens[currentToken].type === 'AND') {
      currentToken++ // consume AND
      const right = parseNot()
      if (!right) return null

      left = {
        type: 'AND',
        left,
        right,
      }
    }

    return left
  }

  function parseNot(): WhereClause | null {
    if (currentToken < tokens.length && tokens[currentToken].type === 'NOT') {
      currentToken++ // consume NOT
      const operand = parsePrimary()
      if (!operand) return null

      return {
        type: 'NOT',
        right: operand,
      }
    }

    return parsePrimary()
  }

  function parsePrimary(): WhereClause | null {
    if (currentToken >= tokens.length) return null

    const token = tokens[currentToken]

    // Handle parentheses
    if (token.type === 'LPAREN') {
      currentToken++ // consume (
      const expression = parseExpression()
      if (!expression) return null

      if (currentToken >= tokens.length || tokens[currentToken].type !== 'RPAREN') {
        return null // missing closing parenthesis
      }

      currentToken++ // consume )

      return {
        ...expression,
        parentheses: true,
      }
    }

    // Handle condition
    if (token.type === 'FIELD') {
      return parseCondition()
    }

    return null
  }

  function parseCondition(): WhereClause | null {
    if (currentToken >= tokens.length || tokens[currentToken].type !== 'FIELD') {
      return null
    }

    const fieldToken = tokens[currentToken]
    currentToken++ // consume field

    if (currentToken >= tokens.length || tokens[currentToken].type !== 'OPERATOR') {
      return null
    }

    const operatorToken = tokens[currentToken]
    currentToken++ // consume operator

    // Parse value based on operator
    let value: any = null

    if (operatorToken.value === 'IN') {
      // Handle IN clause
      if (currentToken >= tokens.length || tokens[currentToken].type !== 'LPAREN') {
        return null
      }

      currentToken++ // consume (

      const values: string[] = []
      while (currentToken < tokens.length && tokens[currentToken].type !== 'RPAREN') {
        if (tokens[currentToken].type === 'VALUE') {
          values.push(unquoteTokenValue(tokens[currentToken].value))
          currentToken++
        } else if (tokens[currentToken].type === 'COMMA') {
          currentToken++ // consume comma
        } else {
          return null // invalid IN clause
        }
      }

      if (currentToken >= tokens.length || tokens[currentToken].type !== 'RPAREN') {
        return null
      }

      currentToken++ // consume )
      value = values
    } else {
      // Handle single value
      if (currentToken >= tokens.length || tokens[currentToken].type !== 'VALUE') {
        return null
      }

      const valueToken = tokens[currentToken]
      value = unquoteTokenValue(valueToken.value)
      currentToken++
    }

    return {
      type: 'CONDITION',
      condition: {
        field: fieldToken.value,
        operator: operatorToken.value,
        value,
      },
    }
  }

  return parseExpression()
}

// Filter function to remove the filter with {{searchString}}
function filterSearchConditions(ast: WhereClause | null, searchString: string): WhereClause | null {
  if (!ast) return null

  // If it's a condition, check if it's search-related
  if (ast.type === 'CONDITION' && ast.condition) {
    const { value } = ast.condition

    // Check if the condition contains {{searchString}} placeholder
    const isSearchRelated =
      (typeof value === 'string' && value.includes('{{searchString}}')) ||
      (Array.isArray(value) && value.some((v) => typeof v === 'string' && v.includes('{{searchString}}')))

    if (isSearchRelated) {
      return null // Remove search-related conditions
    }

    return ast // Keep non-search conditions
  }

  // Recursively filter children
  const left = filterSearchConditions(ast.left || null, searchString)
  const right = filterSearchConditions(ast.right || null, searchString)

  // If both children are null, return null
  if (!left && !right) return null

  // If only one child is null, return the other
  if (!left) return right
  if (!right) return left

  // Return the node with filtered children
  return {
    ...ast,
    left,
    right,
  }
}

/**
 * Determines if a value should be quoted in SOQL/SOSL
 * Booleans (true/false), numbers, and NULL should not be quoted
 */
function shouldQuoteValue(value: string): boolean {
  if (!value) return true

  const lowerValue = value.toLowerCase()

  // Check for boolean values
  if (lowerValue === 'true' || lowerValue === 'false') {
    return false
  }

  // Check for NULL
  if (lowerValue === 'null') {
    return false
  }

  // Check for numeric values (integer or decimal)
  if (/^-?\d+(\.\d+)?$/.test(value)) {
    return false
  }

  return true
}

/**
 * Formats a value for SOQL/SOSL, adding quotes if needed
 */
function formatValue(value: string): string {
  return shouldQuoteValue(value) ? `'${value}'` : value
}

function astToSosl(ast: WhereClause | null, parentType?: string): string {
  if (!ast) return ''

  if (ast.type === 'CONDITION' && ast.condition) {
    const { field, operator, value } = ast.condition

    if (operator === 'IN' && Array.isArray(value)) {
      const values = value.map((v) => formatValue(v)).join(', ')
      return `${field} ${operator} (${values})`
    }

    if (operator === 'LIKE') {
      return `${field} ${operator} '${value}'`
    }

    return `${field} ${operator} ${formatValue(value)}`
  }

  if (ast.type === 'AND') {
    const left = astToSosl(ast.left || null, 'AND')
    const right = astToSosl(ast.right || null, 'AND')

    if (!left || !right) return left || right || ''

    // Wrap in parentheses if this node was originally wrapped or if parent is OR
    const result = `${left} AND ${right}`
    return ast.parentheses || parentType === 'OR' ? `(${result})` : result
  }

  if (ast.type === 'OR') {
    const left = astToSosl(ast.left || null, 'OR')
    const right = astToSosl(ast.right || null, 'OR')

    if (!left || !right) return left || right || ''

    // Wrap in parentheses if this node was originally wrapped
    const result = `${left} OR ${right}`
    return ast.parentheses ? `(${result})` : result
  }

  if (ast.type === 'NOT') {
    const operand = astToSosl(ast.right || null, 'NOT')
    if (!operand) return ''

    // Wrap in parentheses if this node was originally wrapped
    const result = `NOT ${operand}`
    return ast.parentheses ? `(${result})` : result
  }

  return ''
}

export const convertToSosl = (soqlQuery: string, searchString: string, searchInField: string = 'ALL') => {
  // Extract fields from SELECT clause
  const selectMatch = soqlQuery.match(/SELECT\s+([\s\S]+?)\s+FROM/i)
  if (!selectMatch) return null

  const fields = selectMatch[1].split(',').map((field) => field.trim())

  // Extract object name from 'FROM' clause
  const fromMatch = soqlQuery.match(/FROM\s+(\w+)/i)
  if (!fromMatch) return null
  const objectName = fromMatch[1]

  // Extract WHERE clause
  const whereMatch = soqlQuery.match(/WHERE\s+([\s\S]+?)(?:\s+ORDER\s+BY|\s+GROUP\s+BY|\s+LIMIT|$)/i)
  const whereClause = whereMatch ? whereMatch[1].trim() : null

  // Extract ORDER BY clause
  const orderByMatch = soqlQuery.match(/ORDER\s+BY\s+([\s\S]+?)(?:\s+GROUP\s+BY|\s+LIMIT|$)/i)
  const orderByClause = orderByMatch ? orderByMatch[1].trim() : ''

  // Extract LIMIT clause
  const limitMatch = soqlQuery.match(/LIMIT\s+(\d+)/i)
  const limitClause = limitMatch ? limitMatch[1] : ''

  let soslWhereClause = ''

  if (whereClause) {
    // Parse WHERE clause into AST
    const ast = parseWhereClause(whereClause)

    if (ast) {
      // Filter out search-related conditions
      const filteredAst = filterSearchConditions(ast, searchString)

      if (filteredAst) {
        // Convert back to SOSL
        const wherePart = astToSosl(filteredAst)
        if (wherePart) {
          soslWhereClause = ` WHERE ${wherePart}`
        }
      }
    }
  }

  // Construct the ORDER BY part for SOSL (if present)
  const soslOrderByClause = orderByClause ? ` ORDER BY ${orderByClause}` : ''

  // Construct the LIMIT part for SOSL (if present)
  const soslLimitClause = limitClause ? ` LIMIT ${limitClause}` : ''

  return `FIND {${searchString}} IN ${searchInField} FIELDS RETURNING ${objectName}(${fields.join(', ')}${soslWhereClause}${soslOrderByClause}${soslLimitClause})`
}
